import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import type { Document } from '@contentful/rich-text-types';
import { Col, Row } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { cn } from '@opendoor/atoms/utils';
import { IconArrowRight } from '@tabler/icons-react';
import type { EntryComponent, IRenderContext } from 'cms/entries/entries';
import { Subhead, TitleWithAccent } from 'components/landing-pages-v2/shared/Typography';
import { Divider } from 'components/shared/Divider';
import type { ILpComponentFaqDisplay, ILpComponentFaqDisplayFields } from 'declarations/contentful';
import Head from 'next/head';
import type { FAQPage, WithContext } from 'schema-dts';

import RichTextRenderer from '../../cms/renderer';
import Grid from './Grid';

const isDocument = (value: unknown): value is Document =>
  typeof value === 'object' && value !== null && (value as Document).nodeType === 'document';

const buildFaqSchema = (
  faQs: NonNullable<ILpComponentFaqDisplayFields['faQs']>,
): WithContext<FAQPage> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faQs
    .filter((faq) => Boolean(faq?.fields))
    .map((faq) => {
      const { title, body } = faq.fields as { title?: string; body?: unknown };
      return {
        '@type': 'Question',
        name: title ?? '',
        acceptedAnswer: {
          '@type': 'Answer',
          text: isDocument(body) ? documentToPlainTextString(body) : '',
        },
      };
    }),
});

const RenderFAQsDisplay = (
  entry: ILpComponentFaqDisplay,
  _loaderProps?: unknown,
  context?: IRenderContext,
) => {
  const { fields } = entry;

  const faqSchema = fields?.faQs && fields.faQs.length > 0 ? buildFaqSchema(fields.faQs) : null;
  const entryLookup = context?.entryLookup ?? { block: {}, inline: {} };
  const analyticsName = `cosmos-landing-page-faqs-display-${fields?.analyticsId}`;

  const viewAllLink = fields?.viewMoreLinkUrl ? (
    <Link aria-label="View more FAQs" analyticsName={analyticsName} href={fields?.viewMoreLinkUrl}>
      View all FAQ
      <IconArrowRight size={16} aria-hidden="true" />
    </Link>
  ) : null;
  return (
    <>
      {faqSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqSchema).replace(/</g, '\\u003c'),
            }}
          />
        </Head>
      )}
      <Col analyticsName={analyticsName} trackImpression>
        <Grid backgroundColor="$backgroundSecondary" py="$32x">
          <Col className="col-span-12 w-full content-center pr-2 md:flex-row md:pr-5 lg:pr-2">
            <Col className="w-full gap-14">
              <Row className="w-full items-end justify-between">
                <TitleWithAccent title={fields?.title} maxWidth="unset" mb="$0x" />
                <Col className="hidden pb-2 md:flex">{viewAllLink}</Col>
              </Row>
              <Col>
                <Col>
                  {fields?.faQs?.map((faq: any, idx) => {
                    return (
                      <Col key={idx}>
                        <Col className={cn('py-10 md:py-5 lg:py-12', idx === 0 && 'hidden')}>
                          <Divider />
                        </Col>
                        <Col className="w-full gap-5 md:flex-row md:items-start md:gap-[15%] xl:pr-22">
                          <Col className="w-full md:w-[35%]">
                            <Subhead $largerThanMD={{ fontSize: 24 }}>{faq?.fields?.title}</Subhead>
                          </Col>
                          <Col className="w-full md:-mt-5 md:w-[55%]">
                            <RichTextRenderer.Component
                              body={faq?.fields?.body}
                              idToLoaderData={{}}
                              entryLookup={entryLookup}
                            />
                          </Col>
                        </Col>
                      </Col>
                    );
                  })}
                </Col>
              </Col>
              <Col className="md:hidden">{viewAllLink}</Col>
            </Col>
          </Col>
        </Grid>
      </Col>
    </>
  );
};

const FAQsDisplay: EntryComponent<ILpComponentFaqDisplay> = {
  render: RenderFAQsDisplay,
};

export default FAQsDisplay;
